// Tiny text helpers shared by EventCard (#39 tile preview) and the detail page.

// Trim an event abstract down to ~maxChars for the tile excerpt. Splits on the
// nearest word boundary inside the budget so we never end mid-word, then adds
// an ellipsis. Returns null when the input is empty so the caller can hide the
// element rather than rendering a stray ellipsis.
export function excerpt(text, maxChars = 180) {
  if (text == null) return null
  const trimmed = String(text).trim()
  if (!trimmed) return null
  if (trimmed.length <= maxChars) return trimmed
  // Walk back from the budget to the previous space so we keep whole words.
  // Allow a small overshoot (the next 30 chars) when the next space is close,
  // to avoid lopping off most of a long final word.
  const hardCut = trimmed.slice(0, maxChars)
  const lastSpace = hardCut.lastIndexOf(' ')
  const cut = lastSpace > maxChars - 40 ? hardCut.slice(0, lastSpace) : hardCut
  return `${cut.trimEnd()}…`
}

// #307 compatibility. The events API is moving `conference` from a plain
// string to a nested object, and both shapes are live at once: the frozen v1
// SPA and the current v2 build share one backend, and svc ships before webapp.
//
// Reading `event.conference` directly is not merely wrong once the object
// arrives — React throws "Objects are not valid as a React child" and the
// whole card tree unmounts. This resolves either shape to a display string.
//
// Order matters: `conferenceName` is the deprecated compatibility field the
// new API sends, so prefer it, then the nested object's name, then the legacy
// bare string.
export function conferenceLabel(event) {
  if (!event) return ''
  if (event.conferenceName) return event.conferenceName
  const c = event.conference
  if (!c) return ''
  if (typeof c === 'string') return c
  return c.name || ''
}

// #334: the home-page strapline. It used to hardcode "upcoming", which was
// true only because the calendar could not show anything else. Now that the
// date-range control reaches into the past, the word is a claim the list no
// longer supports, so it is dropped whenever the selected range includes days
// that have already gone by.
//
// `count === null` means the first page has not landed yet — say the general
// thing rather than flashing "0 talks" for a moment.
export function eventCountStrapline(count, pastIncluded = false) {
  if (count == null) return 'Upcoming talks from the Actian Office of the CTO.'
  const noun = count === 1 ? 'talk' : 'talks'
  if (pastIncluded) {
    return count === 0
      ? 'No talks in this range.'
      : `${count} ${noun} from the Actian Office of the CTO in this range.`
  }
  return count === 0
    ? 'Nothing on the schedule right now — check back soon.'
    : `${count} upcoming ${noun} from the Actian Office of the CTO.`
}

// #353: a conference card renders the conference name as its heading and the
// talk title one line below. When a conference holds a single talk those two
// are often the same sentence — measured against production, 3 of 21
// conference-attached talks were byte-identical to their conference once a
// year is discounted, and the card said the same thing twice.
//
// Returns the title to render for a talk inside a conference, or null when the
// title carries nothing the heading has not already said.
//
// The naive version of this destroys information. An earlier prototype
// suppressed `Sydney Data Engineering Meetup — Make Your AI Scream` outright
// and lost the only interesting half. A title that CONTAINS the conference
// name is not a duplicate of it — only one that equals it, or one that leads
// with it and then says something else.

// Lowercase, drop punctuation and emoji, and drop any four-digit year: a card
// that reads "EMEA Database User Day" over "EMEA Database User Day 2026" is
// repeating itself, and the year is not what makes the second line worth a row.
function normaliseTitle(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(19|20)\d{2}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Em dash, en dash, pipe, colon, or a spaced hyphen — the separators a speaker
// actually types between a venue and a talk name. A colon may be unspaced on
// its left ("Mind the Gap: ...") which is why its rule differs.
const TITLE_SEPARATOR = /\s+[—–|]\s+|\s*:\s+|\s+-\s+/

export function talkTitleWithinConference(conferenceName, talkTitle) {
  const conf = normaliseTitle(conferenceName)
  const talk = normaliseTitle(talkTitle)
  // No conference to compare against, or nothing to render: leave it alone.
  if (!conf || !talk) return talkTitle || null
  // The talk IS the conference. The heading already said it.
  if (conf === talk) return null
  // "<conference> — <the actual talk>" keeps only the second half.
  const parts = String(talkTitle).split(TITLE_SEPARATOR)
  if (parts.length > 1) {
    const head = parts[0]
    const rest = String(talkTitle).slice(head.length).replace(TITLE_SEPARATOR, '').trim()
    if (normaliseTitle(head) === conf && normaliseTitle(rest)) return rest
  }
  return talkTitle
}
